import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { api, setCsrfToken, setImpersonationToken } from '@/lib/api'
import type { Account } from '@/types'

interface AuthContextType {
  user: Account | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string, totp_code?: string) => Promise<void>
  register: (data: { email: string; password: string; display_name?: string; invite_token?: string }) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  login: async () => {},
  register: async () => {},
  logout: () => {},
})

function getPostHog(): any {
  return typeof window !== 'undefined' ? (window as any).posthog : null
}

function identifyUser(account: Account) {
  const posthog = getPostHog()
  if (!posthog || posthog.get_explicit_consent_status?.() !== 'granted') return
  posthog.identify(account.id, {
    email: account.email,
    plan: account.plan || 'unknown',
  })
}

function resetUser() {
  const posthog = getPostHog()
  if (!posthog) return
  posthog.reset()
}

function captureEvent(event: string, properties?: Record<string, unknown>) {
  const posthog = getPostHog()
  if (!posthog || posthog.get_explicit_consent_status?.() !== 'granted') return
  posthog.capture(event, properties)
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Account | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const handleAuthError = useCallback(() => {
    setUser(null)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    const url = new URL(window.location.href)
    const impersonateToken = url.searchParams.get('impersonate')
    if (impersonateToken) {
      // CHECKPOINT: Phase 2 security — impersonation token stays in memory only.
      url.searchParams.delete('impersonate')
      window.history.replaceState({}, '', url.toString())
      setImpersonationToken(impersonateToken)
      api.get('/auth/me')
        .then((res) => { setUser(res.data) })
        .catch(handleAuthError)
        .finally(() => {
          setImpersonationToken(null)
          setIsLoading(false)
        })
      return
    }

    // Access token is an httpOnly cookie; withCredentials sends it automatically.
    api.get('/auth/me')
      .then((res) => {
        setUser(res.data)
        identifyUser(res.data)
      })
      .catch(handleAuthError)
      .finally(() => setIsLoading(false))
  }, [handleAuthError])

  const login = useCallback(async (email: string, password: string, totp_code?: string) => {
    const res = await api.post('/auth/login', { email, password, totp_code })
    if (res.data.csrf_token) {
      setCsrfToken(res.data.csrf_token)
    }
    const me = await api.get('/auth/me')
    setUser(me.data)
    identifyUser(me.data)
    captureEvent('logged_in')
  }, [])

  // CHECKPOINT: PRD-01 FR-1.1.1 — Public beta registration with email+password;
  // invite tokens remain supported for legacy invitation links.
  const register = useCallback(async (data: { email: string; password: string; display_name?: string; invite_token?: string }) => {
    const res = await api.post('/auth/register', data)
    if (res.data.csrf_token) {
      setCsrfToken(res.data.csrf_token)
    }
    const me = await api.get('/auth/me')
    setUser(me.data)
    identifyUser(me.data)
    captureEvent('signed_up', { has_invite: !!data.invite_token })
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      // ignore backend errors during logout
    }
    setCsrfToken(null)
    setUser(null)
    resetUser()
    window.location.href = '/login'
  }, [])

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isAuthenticated: !!user, login, register, logout }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
